import {
  takeLatest,
  put,
  select,
  all,
  call,
} from 'redux-saga/effects';
import { message } from 'antd';
import { get, set } from 'lodash';
import { push } from 'connected-react-router';
import { createDashboard } from '@api/dashboards';
import { autoRequest } from '@api/createRequestStore';
import { DASHBOARD_URL, DASHBOARD_TYPE } from '@utils/constants';
import * as dashboardsUIActions from '@store/dashboards/ui/actions';
import baseSelector from '@store/errorDiagnostics/base/selectors';
import { parseDashboardURL } from '@utils';
import viewerSessionsSelector from '@store/viewerSessions/selectors';
import * as actions from './actions';
import * as tabsActions from '../tabs/actions';
import * as listActions from '../list/actions';
import * as types from './types';
import { REMOVE } from '../../tempDashboard/types';
import createSelector from './selectors';

let unsavedDashboardId = null;
function* afterRequest() {
  const state = yield select(createSelector);
  if (!state) {
    return;
  }

  const { payload, response } = state;
  const tab = { ...payload, id: response.id };

  // sd-note: this is where the new saved dashboard is opened after it is saved
  yield all([
    put(tabsActions.triggerReplaceTab(tab, unsavedDashboardId, response.id)),
    put(listActions.getList()),
    put(dashboardsUIActions.changeSaveDashboardPromptVisibility(false, tab.id)),
    put(push(parseDashboardURL(tab))),
  ]);
}

function* afterCreated() {
  yield put(listActions.getList());
}

function* initRequest() {
  yield put(actions.initRequest());
}

function* creating({
  payload = {},
  unsavedId,
  createThenMakeDefault,
  makeDefaultFn,
}) {
  yield actions.fetching();
  unsavedDashboardId = unsavedId;
  try {
    const res = yield autoRequest(createDashboard, types.SCOPE, payload, afterRequest);
    if (createThenMakeDefault) {
      makeDefaultFn(res);
    }
    if (!createThenMakeDefault) {
      message.success(`Dashboard "${res.dashboard}" has been saved successfully`);
    }
  } catch (error) {
    message.error(error.message);
  }
}

function* createByDashboardType({
  dashboardName,
  dashboard,
  dashboardType,
  tempId,
  createThenMakeDefault,
  makeDefaultFn,
}) {
  yield put(actions.fetching());
  const payload = {
    name: dashboardName,
    dashboardTypeId: dashboardType,
    config: dashboard,
    isSaved: true,
  };

  // Fix empty selectedMetrics for ERROR_DIAGNOSTICS.
  if (
    get(payload, ['config', 'viewerSessionsTable', 'selectedMetrics'], []).length === 0
    && dashboardType === DASHBOARD_TYPE.ERROR_DIAGNOSTICS
  ) {
    const viewerSessionsStore = yield select(viewerSessionsSelector);
    const baseStore = yield select(baseSelector);
    const selectedMetrics = get(
      viewerSessionsStore,
      [baseStore.currentErrorCode, 'selectedMetrics'],
      [],
    );
    set(payload, ['config', 'viewerSessionsTable', 'selectedMetrics'], selectedMetrics);
  }

  try {
    const res = yield autoRequest(createDashboard, types.SCOPE, payload, afterCreated);
    const { id } = res;
    if (createThenMakeDefault) {
      makeDefaultFn(res);
    }
    yield put(dashboardsUIActions.toggleUpdatePopupVisible(false, id));
    if (!createThenMakeDefault) {
      yield call(message.success, 'Successfully created!', 0.5);
    }
    yield put({
      type: REMOVE,
      key: tempId,
    });
    yield put(push(`${DASHBOARD_URL[dashboardType]}${id}`));
  } catch (error) {
    message.error(error.message);
  }
}

function* watchInitRequest() {
  yield takeLatest([
    types.INIT,
  ], initRequest);
}

function* watchCreating() {
  yield takeLatest([
    types.CREATE_DASHBOARD,
  ], creating);
}

function* watchCreateByDashboardType() {
  yield takeLatest([
    types.CREATE_DASHBOARD_BY_DASHBOARD_TYPE,
  ], createByDashboardType);
}

export default [
  watchInitRequest,
  watchCreating,
  watchCreateByDashboardType,
];
